<template>
  <Modal v-model="visible" title="AI 生成话术" width="640px" :mask-closable="false">
    <div class="ai-generate-container">
      <div class="section">
        <h3>上传话术文档（CSV）</h3>
        <p class="desc">上传 CSV 文档至 AI 服务，然后自动生成仅包含 scenes 和 intents 的话术数据。</p>
        <div class="upload-area">
          <Upload ref="csvUpload" :before-upload="beforeCsvUpload" 
            :show-upload-list="false">
            <Button type="primary" :loading="uploading">
              <Icon type="md-cloud-upload" />
              选择 CSV 文件
            </Button>
          </Upload>
          <p class="upload-tip">仅支持 .csv，文件将先上传到 AI 服务再执行生成。</p>
        </div>
      </div>

      <div class="section">
        <h3>导入方式</h3>
        <RadioGroup v-model="importMode" type="button">
          <Radio label="replace">替换当前 scenes 与 intents</Radio>
          <Radio label="append">追加到当前 scenes 与 intents</Radio>
        </RadioGroup>
      </div>

      <div class="section">
        <Button type="success" :loading="running" @click="runAi" :disabled="!uploadedFileId">
          <Icon type="md-play" />
          执行生成
        </Button>
      </div>
    </div>

    <div slot="footer">
      <Button @click="handleCancel" :disabled="uploading || running">关闭</Button>
    </div>
  </Modal>
</template>

<script>
import axios from 'axios'
import { mapState, mapActions } from 'vuex'
import { addNodeId } from '@/utils/nodeIdPool'

export default {
  name: 'AiGenerateModal',
  props: {
    value: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      uploading: false,
      running: false,
      uploadedFileId: '',
      importMode: 'replace',
      pendingFile: null
    }
  },
  computed: {
    ...mapState('scene', ['scene', 'sceneId']),
    ...mapState('user', ['company']),
    visible: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      }
    },
    userMarker() {
      return `scene-${this.sceneId}`
    },
    difyToken() {
      const companyId = (this.company && this.company.id) || this.company?.company_id || null
      const tokens = {
        974: 'Bearer app-TAQQdRWMauBga4l4Z9VTLYOv',
        6951: 'Bearer app-MmfIZ5oNLWnsxr5OFGqs1Xam',
        108257: 'Bearer app-Z6Co0Aoo1DZtKi7tlpDb0Mrz',
        106764: 'Bearer app-27Xud2Be9OiDHn9zoRq0VTnM'
      }
      return tokens[companyId] || 'Bearer app-27Xud2Be9OiDHn9zoRq0VTnM'
    }
  },
  methods: {
    ...mapActions('scene', ['saveScene']),

    handleCancel() {
      this.visible = false
    },

    beforeCsvUpload(file) {
      this.pendingFile = file
      this.uploadToDify(file)
      return false
    },

    async uploadToDify(file) {
      this.uploading = true
      try {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('user', this.userMarker)

        const resp = await axios.post(
          'https://dify.gkscrm.com/v1/files/upload',
          formData,
          {
            headers: {
              Authorization: this.difyToken
            }
          }
        )
        // Dify returns file meta; accept both raw or wrapped
        const data = resp.data || {}
        const fileId = data.id || (data.data && data.data.id)
        if (!fileId) throw new Error('上传失败：未返回文件ID')
        this.uploadedFileId = fileId
        this.$Message.success('文件上传成功')
      } catch (err) {
        console.error('上传CSV失败:', err)
        this.$Message.error('上传失败，请重试')
      } finally {
        this.uploading = false
      }
    },

    async runAi() {
      if (!this.uploadedFileId) return
      this.running = true
      try {
        const payload = {
          response_mode: 'streaming',
          user: this.userMarker,
          inputs: {
            csv: {
              type: "document",
              upload_file_id: this.uploadedFileId,
              transfer_method: "local_file"
            }
          }
        }

        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), 120000)
        const resp = await fetch('https://dify.gkscrm.com/v1/workflows/run', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: this.difyToken
          },
          body: JSON.stringify(payload),
          signal: controller.signal
        })
        clearTimeout(timeoutId)
        if (!resp.ok || !resp.body) throw new Error('网络错误或无流式返回')

        const finalData = await this.readSSE(resp.body)
        console.log(finalData, 'finalData')
        // if (!finalData || !finalData.data || !finalData.data.outputs) throw new Error('AI 返回结果为空')
        const parsed = this.parseWorkflowResult(finalData)
        console.log(parsed, 'parsed')
        if (!parsed) throw new Error('AI 返回结果为空或格式不正确')

        const { scenes: newScenes = [], intents: newIntents = [] } = parsed

        if (this.importMode === 'replace') {
          await this.saveScene({
            ...this.scene,
            scenes: Array.isArray(newScenes) ? newScenes : [],
            intents: Array.isArray(newIntents) ? newIntents : []
          })
        } else {
          const merged = this.appendScenesAndIntents(this.scene, {
            scenes: Array.isArray(newScenes) ? newScenes : [],
            intents: Array.isArray(newIntents) ? newIntents : []
          })
          await this.saveScene(merged)
        }

        this.$Message.success('AI 生成并导入成功')
        this.visible = false
        // 刷新当前路由页面
        this.$router.go(0)
      } catch (err) {
        console.error('执行AI生成失败:', err)
        this.$Message.error('AI 生成失败，请检查文件与网络')
      } finally {
        this.running = false
      }
    },

    async readSSE(readableStream) {
      const reader = readableStream.getReader()
      const decoder = new TextDecoder('utf-8')
      let buffer = ''
      let lastData = null

      let isDone = false
      while (!isDone) {
        const chunk = await reader.read()
        isDone = chunk.done === true
        if (isDone) break
        buffer += decoder.decode(chunk.value, { stream: true })

        const events = buffer.split('\n\n')
        buffer = events.pop() || ''
        events.forEach(evt => {
          const lines = evt.split('\n')
          lines.forEach(line => {
            const s = line.trim()
            if (s.startsWith('data:')) {
              const jsonStr = s.replace(/^data:\s*/, '')
              if (jsonStr === '[DONE]') return
              try {
                const obj = JSON.parse(jsonStr)
                lastData = obj
              } catch (e) {
                // ignore partial frames
              }
            }
          })
        })
      }

      return lastData || {}
    },

    parseWorkflowResult(data) {
      // Try common Dify structures
      try {
        if (!data) return null
        // direct scenes/intents
        if (data.scenes || data.intents) return data
        // outputs.result as JSON string or object
        const outputs = data.outputs || (data.data && data.data.outputs)
        if (outputs) {
          const result = outputs.scene || outputs
          if (typeof result === 'string') {
            try {
              return JSON.parse(result)
            } catch (e) { /* ignore */ }
          } else if (result && (result.scenes || result.intents)) {
            return result
          }
        }
        // data as JSON string
        if (typeof data === 'string') {
          return JSON.parse(data)
        }
      } catch (e) {
        console.warn('解析AI结果异常', e)
      }
      return null
    },

    appendScenesAndIntents(baseScene, incoming) {
      const result = { ...baseScene }
      const nodeIdPool = { scenes: 'F', intents: 'I' }

      // scenes
      const baseScenes = Array.isArray(baseScene.scenes) ? baseScene.scenes.slice() : []
      const incScenes = Array.isArray(incoming.scenes) ? incoming.scenes : []
      incScenes.forEach(item => {
        baseScenes.push({
          ...item,
          id: addNodeId(nodeIdPool.scenes)
        })
      })
      result.scenes = baseScenes

      // intents
      const baseIntents = Array.isArray(baseScene.intents) ? baseScene.intents.slice() : []
      const incIntents = Array.isArray(incoming.intents) ? incoming.intents : []
      incIntents.forEach(item => {
        baseIntents.push({
          ...item,
          id: addNodeId(nodeIdPool.intents)
        })
      })
      result.intents = baseIntents

      return result
    }
  }
}
</script>

<style lang="scss" scoped>
.ai-generate-container {
  .section {
    margin-bottom: 16px;
    padding: 16px;
    border: 1px solid #e8eaec;
    border-radius: 6px;
    background-color: #fafafa;

    h3 {
      margin: 0 0 8px 0;
      font-size: 16px;
      font-weight: 600;
      color: #17233d;
    }

    .desc {
      margin: 0 0 12px 0;
      color: #808695;
      font-size: 14px;
      line-height: 1.5;
    }

    .upload-area {
      .upload-tip {
        margin: 8px 0 0 0;
        color: #c5c8ce;
        font-size: 12px;
      }
    }
  }
}
</style>
